import {GetStaticPaths, GetStaticProps} from 'next';

import {Layout, LayoutProps} from 'src/components/layout';
import {
  ProductPageTemplateProps,
  ProductPageTemplate,
} from 'src/components/product-page';
import {
  getCombinedRegionsAndLocales,
  isSupportedLocale,
  isValidRegion,
} from 'src/config';
import {
  getContentfulProducts,
  getContentfulProductBySlug,
} from 'src/features/product/data';
import {PageParams} from 'src/types';
import {isAllowedRegion} from 'src/utils/contentful';

interface ProductIndexPageParams extends PageParams {
  product_slug: string;
}

interface Path {
  params: ProductIndexPageParams;
}

type ProductIndexPageProps = ProductPageTemplateProps & LayoutProps;

export const getStaticPaths: GetStaticPaths<
  ProductIndexPageParams
> = async () => {
  const products = getContentfulProducts();

  const paths: Path[] = [];

  products.forEach((product) => {
    const {
      fields: {slug, availableInRegions},
    } = product;

    getCombinedRegionsAndLocales().forEach(({region, locale}) => {
      if (isAllowedRegion(region, availableInRegions)) {
        paths.push({
          params: {region: region, locale, product_slug: slug},
        });
      }
    });
  });

  return {paths, fallback: false};
};

export const getStaticProps: GetStaticProps<
  ProductIndexPageProps,
  ProductIndexPageParams
> = async (ctx) => {
  const {params} = ctx;

  if (!params) {
    throw new Error('Could not find params for product page.');
  }

  const {locale, region, product_slug} = params;

  if (!isSupportedLocale(locale)) {
    throw new Error(`Invalid locale. Received: ${locale}`);
  }

  if (!isValidRegion(region)) {
    throw new Error(`Invalid region. Received: ${region}`);
  }

  const product = getContentfulProductBySlug({slug: product_slug, locale});

  if (!product) {
    throw new Error(
      `Failed to find product with slug: ${product_slug} and locale: ${locale}`
    );
  }

  const props: ProductIndexPageProps = {
    product: product,
    productVariant: product.fields.variants[0],
    pageContext: {
      region,
      locale,
    },
    seo: {
      title: product.fields.name,
      description: product.fields.seoDescription,
      includeInRegions: ['all'],
      slugPrefix: '/products',
      slug: product_slug,
      noIndex: product.fields.hidden ?? false,
    },
  };

  return {props};
};

const ProductIndexPage: React.FC<
  React.PropsWithChildren<ProductIndexPageProps>
> = (props) => {
  const {pageContext, seo, ...productProps} = props;

  return (
    <Layout pageContext={pageContext} seo={seo}>
      <ProductPageTemplate {...productProps} />
    </Layout>
  );
};

export default ProductIndexPage;
